import React, { useState, useEffect } from 'react';
import { 
  Key, RefreshCw, Copy, Check, Mail, Calendar, 
  Hash, User, ShoppingCart, Loader2, Plus, Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PremiumCodesAdmin = () => {
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState({ total: 0, used: 0, unused: 0 });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newCodeEmail, setNewCodeEmail] = useState('');
  const [newCodeOrderId, setNewCodeOrderId] = useState('');
  const [showNewCodeForm, setShowNewCodeForm] = useState(false);

  const loadCodes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/thesis-checker/premium/codes`);
      const data = await response.json();
      setCodes(data.codes || []);
      setStats({
        total: data.total || 0,
        used: data.used || 0,
        unused: data.unused || 0
      });
    } catch (err) {
      console.error('Failed to load codes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCodes();
  }, []);

  const generateCode = async () => {
    if (!newCodeEmail.trim()) return;
    
    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/thesis-checker/premium/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newCodeEmail.trim(),
          order_id: newCodeOrderId.trim() || null
        })
      });
      
      if (response.ok) {
        setNewCodeEmail('');
        setNewCodeOrderId('');
        setShowNewCodeForm(false);
        loadCodes();
      }
    } catch (err) {
      console.error('Failed to generate code:', err);
    } finally {
      setGenerating(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const filteredCodes = codes.filter(code => 
    code.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.order_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Key className="w-8 h-8 text-purple-600" />
              Premium-Codes
            </h1>
            <p className="text-gray-600 mt-1">Arbeit-Checker Premium-Code Verwaltung</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadCodes}
              disabled={loading}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </button>
            <button
              onClick={() => setShowNewCodeForm(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Neuer Code
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">Codes gesamt</div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="text-3xl font-bold text-green-600">{stats.unused}</div>
            <div className="text-sm text-gray-600">Unbenutzt</div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="text-3xl font-bold text-purple-600">{stats.used}</div>
            <div className="text-sm text-gray-600">Verwendet</div>
          </div>
        </div>

        {/* New Code Form */}
        {showNewCodeForm && (
          <div className="bg-white rounded-xl p-6 border border-purple-200 mb-6">
            <h3 className="font-semibold text-gray-800 mb-4">Neuen Code generieren</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail *</label>
                <input
                  type="email"
                  value={newCodeEmail}
                  onChange={(e) => setNewCodeEmail(e.target.value)}
                  placeholder="kunde@email.ch"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bestellnummer (optional)</label>
                <input
                  type="text"
                  value={newCodeOrderId}
                  onChange={(e) => setNewCodeOrderId(e.target.value)}
                  placeholder="z.B. 100001234"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={generateCode}
                disabled={!newCodeEmail.trim() || generating}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Code generieren
              </button>
              <button
                onClick={() => setShowNewCodeForm(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Code, E-Mail oder Bestellnummer suchen..."
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>

        {/* Codes Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Code</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">E-Mail</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Bestellung</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Erstellt</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Nutzung</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Lade Codes...
                  </td>
                </tr>
              ) : filteredCodes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    Keine Codes gefunden
                  </td>
                </tr>
              ) : (
                filteredCodes.map((code, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <code className="px-2 py-1 bg-purple-100 text-purple-700 rounded font-mono text-sm">
                        {code.code}
                      </code>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Mail className="w-4 h-4 text-gray-400" />
                        {code.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {code.order_id ? (
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <ShoppingCart className="w-4 h-4 text-gray-400" />
                          {code.order_id}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(code.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          code.uses_count >= code.max_uses
                            ? 'bg-red-100 text-red-700'
                            : code.used
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-green-100 text-green-700'
                        }`}>
                          {code.uses_count || 0} / {code.max_uses || 3}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => copyCode(code.code)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Code kopieren"
                      >
                        {copiedCode === code.code ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <h4 className="font-semibold text-blue-800 mb-2">Automatische Code-Generierung</h4>
          <p className="text-sm text-blue-700">
            Das Magento-Modul <code className="bg-blue-100 px-1 rounded">Adag_PremiumCodeGenerator</code> generiert 
            automatisch einen Code, wenn ein Kunde das Produkt "Arbeit-Checker Premium-Analyse" kauft. 
            Der Code wird direkt per E-Mail an den Kunden gesendet.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PremiumCodesAdmin;
